import React, { useEffect } from 'react'
import { FieldProps } from 'formik'
import { Icon } from 'react-bulma-components'
import FormField, { FormFieldProps } from './form-field'
import { useCombobox } from 'downshift'
import { get } from 'lodash'
import { SearchIcon } from '../../icons'

export interface AutocompleteFieldItem {
  value: string
  label: string
  renderedLabel?: React.ReactNode
  [key: string]: any
}

/**
 * Presentation-only props shared between the Formik-wired and the controlled variants.
 */
interface AutocompleteBaseProps extends FormFieldProps {
  items: AutocompleteFieldItem[]
  name?: string
  onlySelect?: boolean
  disabled?: boolean
  required?: boolean
  placeholder?: string
  clearOnSelect?: boolean
  showItemsAfterSelect?: boolean
  initialInputValue?: string
  error?: string
  autoComplete?: string
}

/**
 * Props of the controlled (Formik-free) autocomplete.
 *
 * `value` is the currently selected item value (id). `onSelect` is fired whenever
 * the user picks an item from the dropdown. `onInputChange` is fired for every
 * keystroke (useful for free-text mode when `onlySelect` is false).
 */
export interface ControlledAutocompleteProps extends AutocompleteBaseProps {
  value: string
  onSelect: (value: string, item: AutocompleteFieldItem | undefined) => void
  onInputChange?: (inputValue: string) => void
  resetKey?: unknown
}

/**
 * Stand-alone autocomplete component that works without a Formik context.
 *
 * This is the underlying implementation shared with the Formik-wired
 * `AutocompleteField`. It keeps the same UX (downshift combobox, filtering, icon)
 * but exposes a plain controlled API so it can be used outside of Formik.
 */
export const ControlledAutocomplete: React.FunctionComponent<ControlledAutocompleteProps> = ({
  items,
  label,
  help,
  required,
  disabled = false,
  placeholder,
  onlySelect,
  clearOnSelect,
  showItemsAfterSelect,
  initialInputValue,
  error,
  name,
  autoComplete = 'off',
  value,
  onSelect,
  onInputChange,
  resetKey,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null)

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    getItemProps,
    openMenu,
    reset,
    highlightedIndex,
    setInputValue,
    inputValue,
  } = useCombobox<AutocompleteFieldItem>({
    items: items || [],
    defaultInputValue:
      (items || []).find(item => item.value === value)?.label || initialInputValue || value,
    onSelectedItemChange: changes => {
      const selectedItem = filteredItems[highlightedIndex]
      const nextValue = selectedItem?.value || (!onlySelect && changes.inputValue) || ''
      onSelect(nextValue, selectedItem)

      if (clearOnSelect) {
        setInputValue('')
      } else {
        setInputValue(selectedItem?.label || '')
      }
      if (showItemsAfterSelect) {
        inputRef?.current?.blur()
        inputRef?.current?.focus()
      }
    },
    itemToString: item => (item ? item.label : ''),
    onInputValueChange({ inputValue: nextInputValue }) {
      if (onInputChange) onInputChange(nextInputValue ?? '')
      // Free-text mode: propagate the typed text as the selected value.
      if (!onlySelect) {
        onSelect(nextInputValue ?? '', undefined)
      }
    },
  })

  const inputProps = {
    className: 'input',
    onFocus: () => !isOpen && (items || [])[0] && openMenu(),
    // Prevent parent form submission when pressing Enter inside the
    // autocomplete input.
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
      }
    },
  }

  // Reset when the resetKey changes (used by the Formik wrapper when initialValues change).
  useEffect(() => {
    reset()
  }, [resetKey])

  const filteredItems = React.useMemo(
    () =>
      (items || []).filter(
        item =>
          !inputValue || item.label?.toLowerCase().includes(inputValue?.toLowerCase?.() || ''),
      ),
    [items, inputValue],
  )

  return (
    <FormField label={label} error={error} required={required} help={help}>
      <Icon align="left" p={2}>
        <SearchIcon />
      </Icon>
      <div {...getComboboxProps()}>
        <input
          {...getInputProps({ ...inputProps, disabled, ref: inputRef, required })}
          placeholder={placeholder}
          name={name}
          data-lpignore="true"
          data-form-type="other"
          className="input"
          autoComplete={autoComplete}
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
        />
      </div>
      <div {...getMenuProps()}>
        {isOpen && (
          <ul
            style={{
              maxHeight: `250px`,
              overflow: `scroll`,
              position: `absolute`,
              zIndex: 10,
              width: `100%`,
            }}
            className="filter-dropdown"
          >
            {filteredItems.map(item => {
              const filteredItemIndex = filteredItems.findIndex(i => i.value === item.value)
              return (
                <li
                  key={`${item.value}-${item.field}-${item.count}`}
                  {...getItemProps({
                    item,
                    index: filteredItemIndex,
                    disabled: item.disabled,
                    className: [
                      `py-2 has-text-left pl-2 pr-1`,
                      highlightedIndex === filteredItemIndex
                        ? `has-background-primary`
                        : `has-background-white`,
                      item.disabled ? `has-background-grey-lighter` : '',
                    ].join(' '),
                  })}
                >
                  {item.renderedLabel || item.label}
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </FormField>
  )
}

/**
 * Props of the Formik-wired autocomplete field.
 */
interface AutocompleteFieldProps extends AutocompleteBaseProps, FieldProps {
  type: string
}

/**
 * Formik-aware autocomplete field. Thin wrapper around `ControlledAutocomplete`
 * that bridges Formik's `field` / `form` to the controlled API.
 */
const AutocompleteField: React.FunctionComponent<AutocompleteFieldProps> = ({
  field,
  form,
  items,
  label,
  disabled = false,
  help,
  required,
  onlySelect,
  placeholder,
  clearOnSelect,
  showItemsAfterSelect,
  initialInputValue,
}) => {
  return (
    <ControlledAutocomplete
      items={items}
      label={label}
      help={help}
      required={required}
      disabled={disabled}
      placeholder={placeholder}
      onlySelect={onlySelect}
      clearOnSelect={clearOnSelect}
      showItemsAfterSelect={showItemsAfterSelect}
      initialInputValue={
        (items || []).find(item => item.value === get(form.initialValues, field.name))?.label ||
        initialInputValue ||
        (get(form.initialValues, field.name) as string | undefined)
      }
      error={form.errors[field.name] as string}
      name={field.name}
      value={(field.value as string | undefined) ?? ''}
      onSelect={nextValue => {
        form.setFieldValue(field.name, nextValue)
      }}
      resetKey={form.initialValues}
    />
  )
}

export default AutocompleteField
